import { Share } from '@capacitor/share';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LuArrowRight, LuShare2, LuX } from 'react-icons/lu';
import { toast } from 'react-toastify';
import { Button, IconButton } from '../../design-system/components';
import { writeImageToTempFile } from '../../services/share.service';
import { useAchievementsStore } from '../../stores/achievements.store';
import { useCelebrationStore } from '../../stores/celebration.store';
import { useThemeStore } from '../../stores/theme.store';
import { fireAndForget } from '../../utils/fireAndForget';
import { svgToBase64Png } from '../../utils/image-export';
import { embedFontsInSvg } from '../../utils/svg-fonts';
import CourseCompletionCard, { courseCardSvg } from './CourseCompletionCard';
import { GIRAFFE_SRC, LIGHT_SKIN, liveSkin } from './cardSkin';
import DomainCompletionCard, { domainCardSvg } from './DomainCompletionCard';
import EmpathyUnlockCelebration from './EmpathyUnlockCelebration';
import EmpathyUnlockPanel from './EmpathyUnlockPanel';
import JourneyThrowbackPanel from './JourneyThrowbackPanel';
import { celebrationShareText } from './shareText';

type CelebrationType = 'domain' | 'course' | 'empathy-unlock';

/**
 * One backdrop for every step (module card, course card and its throwback,
 * the intro panel): the navy scrim of the new skin in light, black in dark
 * (--ee-celebration-backdrop; a navy veil reads blue on chestnut).
 */
const BACKDROP =
  'fixed inset-0 z-(--ee-z-dialog) flex flex-col items-center justify-center bg-(--ee-celebration-backdrop) backdrop-blur-sm p-2 sm:p-4 animate-fadeIn overflow-auto';

/** Confetti in the redesign palette (orange, light orange, gold, sage, lavender). */
const CONFETTI_COLORS = ['#f47a20', '#ffa056', '#f2b84d', '#71a58d', '#c7b5f0'];

interface ConfettiItem {
  key: number;
  left: string;
  backgroundColor: string;
  animationDelay: string;
  animationDuration: string;
}

function Confetti({ items }: { items: ConfettiItem[] }) {
  return (
    <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden">
      {items.map((item) => (
        <div
          key={item.key}
          className="absolute w-2 h-2 rounded-[2px] animate-confetti"
          style={{
            left: item.left,
            top: '-10px',
            backgroundColor: item.backgroundColor,
            animationDelay: item.animationDelay,
            animationDuration: item.animationDuration,
          }}
        />
      ))}
    </div>
  );
}

interface DomainData {
  domainName?: string;
  pathsCompleted?: number;
  lessonsCompleted?: number;
  averageScore?: number;
  pointsEarned?: number;
  isPremium?: boolean;
}

interface CourseData {
  lessonsCompleted?: number;
  totalLessons?: number;
  totalPoints?: number;
  daysToComplete?: number;
  pathScores?: number[];
  moduleNames?: string[];
  averageScore?: number;
  isPremium?: boolean;
}

interface CelebrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  type?: CelebrationType;
  data?: DomainData | CourseData | Record<string, never>;
}

/**
 * CelebrationModal Component
 *
 * A modal that displays celebration cards for domain/course/empathy-unlock completion.
 * Includes confetti animation, share functionality, and celebration messaging.
 *
 * For empathy-unlock: plays the full-screen sunshine animation first, then shows the info panel.
 */
function CelebrationModal({ isOpen, onClose, type = 'domain', data = {} }: CelebrationModalProps) {
  const [isSharing, setIsSharing] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [courseStep, setCourseStep] = useState<'card' | 'throwback'>('card');
  const [empathyPhase, setEmpathyPhase] = useState<'animation' | 'panel'>('animation');
  const [soundPrePlayed, setSoundPrePlayed] = useState(false);
  // The card on screen follows the theme (dark mode, David 2026-10-27: the
  // module complete card was still light); the shared image stays light.
  // Read from the tokens on every render (a few getPropertyValue calls): the
  // markup string only changes, and the SVG only repaints, if a token did.
  const theme = useThemeStore((s) => s.resolved);
  const skin = theme === 'dark' ? liveSkin() : LIGHT_SKIN;

  // Stable confetti positions — computed once so re-renders don't reshuffle particles
  const confettiItems = useMemo(
    () =>
      [...Array(50)].map((_, i) => ({
        key: i,
        left: `${Math.random() * 100}%`,
        backgroundColor: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        animationDelay: `${Math.random() * 2}s`,
        animationDuration: `${3 + Math.random() * 2}s`,
      })),
    [],
  );

  // Escape key dismissal
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Reset steps when modal opens or type changes
  useEffect(() => {
    if (isOpen) {
      setCourseStep('card');
      if (type === 'empathy-unlock') {
        setEmpathyPhase('animation');
      } else {
        // Reset pre-played flag when showing non-empathy celebrations
        setSoundPrePlayed(false);
      }
    }
  }, [isOpen, type]);

  // Trigger confetti and play sound when modal opens (skip for empathy-unlock — the animation has its own sound)
  useEffect(() => {
    if (isOpen && type !== 'empathy-unlock') {
      setShowConfetti(true);

      // Play celebration sound
      const soundMap: Record<string, string> = {
        domain: '/path_complete.opus',
        course: '/course_complete.opus',
      };
      const soundFile = soundMap[type] || '/path_complete.opus';
      const audio = new Audio(soundFile);
      audio.play().catch((err: unknown) => console.log('Audio play failed:', err));

      // Stop confetti after 5 seconds
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, type]);

  // Start confetti when empathy panel phase begins
  useEffect(() => {
    if (isOpen && type === 'empathy-unlock' && empathyPhase === 'panel') {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, type, empathyPhase]);

  const handleEmpathyAnimationComplete = useCallback(() => {
    setEmpathyPhase('panel');
  }, []);

  // Pre-play choir sound in user gesture context when empathy-unlock is queued next
  const handleContinueLearning = useCallback(() => {
    const queue = useCelebrationStore.getState().celebrationQueue;
    if (queue.length > 0 && queue[0].type === 'empathy-unlock') {
      const audio = new Audio('/choir.opus');
      audio.play().catch((err: unknown) => console.log('Pre-play choir failed:', err));
      setSoundPrePlayed(true);
    }
    onClose();
  }, [onClose]);

  const handleShare = async () => {
    setIsSharing(true);

    try {
      // Get the appropriate SVG element ID based on type
      const svgId =
        type === 'domain' ? 'shareable-domain-completion-card' : 'shareable-course-completion-card';

      const liveSvg = document.getElementById(svgId) as SVGElement | null;

      if (!liveSvg) {
        throw new Error('Celebration card not found. Please try again.');
      }

      // Export a copy: the image and font embedding below must not touch the
      // card on screen. The copy is drawn in the LIGHT skin whatever the
      // screen shows: a shared image stays light (dark mode, DARK-MODE.md §8).
      const svgElement = liveSvg.cloneNode(false) as SVGElement;
      svgElement.innerHTML =
        type === 'domain'
          ? domainCardSvg(data as DomainData, LIGHT_SKIN)
          : courseCardSvg(data as CourseData, LIGHT_SKIN);
      svgElement.setAttribute('data-skin', 'light');

      // Both cards draw the graduated giraffe and the card fonts, which an
      // SVG drawn into a canvas cannot fetch: inline the image as base64.
      const giraffeImg = new Image();
      giraffeImg.crossOrigin = 'anonymous';

      await new Promise<void>((resolve, reject) => {
        giraffeImg.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = giraffeImg.width;
          canvas.height = giraffeImg.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          ctx.drawImage(giraffeImg, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');

          const imageElement = svgElement.querySelector('image');
          if (imageElement) {
            imageElement.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', dataUrl);
            imageElement.setAttribute('href', dataUrl);
          }
          resolve();
        };
        // Offline the giraffe cannot load: share the card without it (the
        // canvas will not fetch the URL left in the copy) rather than nothing.
        giraffeImg.onerror = () => resolve();
        giraffeImg.src = GIRAFFE_SRC;
      });

      // The cards draw in Inter and Space Grotesk, which an SVG drawn into
      // a canvas cannot reach; embed them. Offline, the image still goes
      // out, in the system font.
      await embedFontsInSvg(svgElement);

      // Convert SVG to base64 PNG
      const base64Image = await svgToBase64Png(svgElement, 1080, 1350, 2);

      // Prepare share text based on type (URL provided separately via url parameter)
      const shareText = celebrationShareText(type, data as DomainData & CourseData);

      // Try native sharing first
      const shareAvailable = await Share.canShare();

      if (shareAvailable.value) {
        // Write image to temp file for mobile sharing
        const fileUri = await writeImageToTempFile(
          base64Image,
          `empathease-${type}-completion-${Date.now()}.png`,
        );

        await Share.share({
          title: type === 'domain' ? 'Module Complete!' : 'Course Complete!',
          text: shareText,
          url: 'https://empathease.app',
          dialogTitle: 'Share Your Achievement',
          files: [fileUri],
        });
        toast.success('Achievement shared!');

        // Track share for achievement purposes
        void useAchievementsStore.getState().trackShare();
      } else {
        // Fallback: Download the image
        const link = document.createElement('a');
        link.href = base64Image;
        link.download = `empathease-${type}-completion.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.info('Image downloaded to your device!');

        // Track share for achievement purposes (download counts as share intent)
        void useAchievementsStore.getState().trackShare();
      }
    } catch (error) {
      console.error('Share error:', error);
      if (error instanceof Error && error.message && !error.message.includes('cancel')) {
        toast.error(error.message || 'Failed to share. Please try again.');
      }
    } finally {
      setIsSharing(false);
    }
  };

  if (!isOpen) return null;

  // Empathy unlock: two-phase — animation first, then info panel
  if (type === 'empathy-unlock') {
    // Phase 1: Full-screen sunshine animation (has its own backdrop and sound)
    if (empathyPhase === 'animation') {
      return (
        <EmpathyUnlockCelebration
          isActive={true}
          onComplete={handleEmpathyAnimationComplete}
          skipSound={soundPrePlayed}
        />
      );
    }

    // Phase 2: Info panel with confetti
    return (
      <div className={BACKDROP}>
        {showConfetti ? <Confetti items={confettiItems} /> : null}
        <EmpathyUnlockPanel onClose={onClose} />
        <style>{celebrationStyles}</style>
      </div>
    );
  }

  // Course completion step 2: journey throwback
  if (type === 'course' && courseStep === 'throwback') {
    return (
      <div className={BACKDROP}>
        <JourneyThrowbackPanel onClose={onClose} />
        <style>{celebrationStyles}</style>
      </div>
    );
  }

  // Domain or Course (step 1: card view)
  return (
    <div className={BACKDROP}>
      {/* Confetti effect */}
      {showConfetti ? <Confetti items={confettiItems} /> : null}

      {/* Modal Content Container (the new skin: design-system controls) */}
      <div className="w-full max-w-[95vw] sm:max-w-md md:max-w-[480px] flex flex-col gap-3 my-auto">
        {/* Card with Close Button */}
        <div className="relative rounded-[var(--ee-radius-lg)] overflow-hidden animate-scaleIn bg-[var(--ee-canvas)] shadow-[0_12px_30px_rgba(20,32,51,0.11),0_24px_60px_rgba(0,0,0,0.25)]">
          {/* Close button overlaid on card */}
          <IconButton
            label="Continue learning"
            icon={<LuX />}
            onClick={handleContinueLearning}
            className="absolute top-2.5 right-2.5 z-20"
          />

          {/* Full-size Card */}
          <div className="w-full" style={{ aspectRatio: '4/5' }}>
            {type === 'domain' ? (
              <DomainCompletionCard {...(data as DomainData)} skin={skin} />
            ) : (
              <CourseCompletionCard {...(data as CourseData)} skin={skin} />
            )}
          </div>
        </div>

        {/* Action Buttons Below Card: stacked on a phone, side by side from sm */}
        <div className="flex flex-col gap-2.5 sm:flex-row-reverse">
          <Button
            variant="primary"
            fullWidth
            className="sm:flex-1"
            loading={isSharing}
            leadingIcon={<LuShare2 />}
            onClick={fireAndForget(handleShare)}
          >
            {isSharing ? 'Generating...' : 'Share Achievement'}
          </Button>

          <Button
            variant="secondary"
            fullWidth
            className="sm:flex-1"
            leadingIcon={<LuArrowRight />}
            onClick={type === 'course' ? () => setCourseStep('throwback') : handleContinueLearning}
          >
            {type === 'course' ? 'Continue' : 'Continue Learning'}
          </Button>
        </div>
      </div>

      <style>{celebrationStyles}</style>
    </div>
  );
}

const celebrationStyles = `
  @keyframes confetti {
    0% {
      transform: translateY(0) rotate(0deg);
      opacity: 1;
    }
    100% {
      transform: translateY(100vh) rotate(720deg);
      opacity: 0;
    }
  }

  .animate-confetti {
    animation: confetti linear forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out;
  }

  @keyframes scaleIn {
    from {
      transform: scale(0.9);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  .animate-scaleIn {
    animation: scaleIn 0.4s ease-out;
  }
`;

export default CelebrationModal;
